"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const moment_1 = __importDefault(require("moment"));
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const hooks_1 = require("../../../store/hooks");
const movingCalendarStreamSlice_1 = require("../../../store/movingCalendarStreamSlice");
const movingStreamSelectors_1 = require("../../../store/movingStreamSelectors");
const dateFormat_1 = require("../../../types/dateFormat");
const useCalendarHook = ({ streamId, minCalendarDate, maxCalendarDate, }) => {
    const SEEN_MONTHS_NUMBER = 3;
    const dispatch = (0, hooks_1.useAppDispatch)();
    const [visibleDateRange, setVisibleDateRange] = (0, react_1.useState)({
        startDate: "",
        endDate: "",
    });
    const threeMonthsData = (0, hooks_1.useAppSelector)((state) => (0, movingStreamSelectors_1.selectCalendarData)(state, visibleDateRange.startDate, visibleDateRange.endDate));
    const movingCalendarData = (0, react_redux_1.useSelector)(movingCalendarStreamSlice_1.movingData);
    const [isRightButtonDisabled, setIsRightButtonDisabled] = (0, react_1.useState)(false);
    const [isLeftButtonDisabled, setIsLeftButtonDisabled] = (0, react_1.useState)(false);
    const [dateReference, setDateReference] = (0, react_1.useState)({
        currentStartDate: "",
        currentEndDate: "",
        firstVisibleDataPointDate: "",
        lastVisibleDataPointDate: "",
        direction: undefined,
        triggerDirectionUpdate: 0,
    });
    const updateMoveValue = (direction) => {
        setDateReference((prevState) => (Object.assign(Object.assign({}, prevState), { direction, triggerDirectionUpdate: prevState.triggerDirectionUpdate + 1 })));
    };
    const handleBackwardMovement = (dateMoment) => {
        setIsRightButtonDisabled(false);
        const newEndMoment = dateMoment.subtract(1, "months").endOf("month");
        return newEndMoment;
    };
    const handleForwardMovement = (dateMoment) => {
        setIsLeftButtonDisabled(false);
        let newEndMoment = dateMoment.add(1, "months").endOf("month");
        const maxEndDateMoment = (0, moment_1.default)(maxCalendarDate, dateFormat_1.DateFormat.default);
        if (newEndMoment.isAfter(maxEndDateMoment)) {
            newEndMoment = maxEndDateMoment;
            setIsRightButtonDisabled(true);
        }
        return newEndMoment;
    };
    const getFormattedDateRange = () => {
        if (!movingCalendarData.data || movingCalendarData.data.length === 0) {
            return {
                firstDate: "",
                lastDate: "",
                lastDateNoFormat: "",
            };
        }
        const lastElementIdx = movingCalendarData.data.length - 1;
        const maxEndDate = movingCalendarData.data[lastElementIdx].date;
        const processedMaxEndDate = (0, moment_1.default)(maxEndDate, dateFormat_1.DateFormat.default).format(dateFormat_1.DateFormat.us);
        const firstElementIdx = 0;
        const firstDataPoint = movingCalendarData.data[firstElementIdx].date;
        const processedFirstDataPoint = (0, moment_1.default)(firstDataPoint, dateFormat_1.DateFormat.default).format(dateFormat_1.DateFormat.us);
        return {
            firstDate: processedFirstDataPoint,
            lastDate: processedMaxEndDate,
            lastDateNoFormat: maxEndDate,
        };
    };
    (0, react_1.useEffect)(() => {
        if (!movingCalendarData.data || movingCalendarData.data.length === 0) {
            return;
        }
        try {
            const sortedData = [...movingCalendarData.data].sort((a, b) => (0, moment_1.default)(a.date).valueOf() - (0, moment_1.default)(b.date).valueOf());
            const firstDate = (0, moment_1.default)(sortedData[0].date).format(dateFormat_1.DateFormat.us);
            const lastDate = (0, moment_1.default)(sortedData[sortedData.length - 1].date).format(dateFormat_1.DateFormat.us);
            // Initialize with the last 3 months of data
            const endMoment = (0, moment_1.default)(lastDate, dateFormat_1.DateFormat.us);
            const newStartDate = endMoment
                .clone()
                .date(1)
                .subtract(SEEN_MONTHS_NUMBER - 1, "months")
                .format(dateFormat_1.DateFormat.us);
            const newEndDate = endMoment.format(dateFormat_1.DateFormat.us);
            setVisibleDateRange({
                startDate: newStartDate,
                endDate: newEndDate,
            });
            // Disable right arrow if we're at the latest data
            const maxEndDateMoment = (0, moment_1.default)(maxCalendarDate, dateFormat_1.DateFormat.default);
            setIsRightButtonDisabled(endMoment.isSameOrAfter(maxEndDateMoment, "day"));
            setDateReference((prevState) => (Object.assign(Object.assign({}, prevState), { currentStartDate: newStartDate, currentEndDate: newEndDate, firstVisibleDataPointDate: newStartDate, lastVisibleDataPointDate: newEndDate })));
        }
        catch (error) {
            console.error("Error processing calendar data:", error);
        }
    }, [movingCalendarData, maxCalendarDate]);
    (0, react_1.useEffect)(() => {
        if (!dateReference.currentEndDate || !dateReference.direction)
            return;
        const dateMoment = (0, moment_1.default)(dateReference.currentEndDate, dateFormat_1.DateFormat.us);
        let newEndMoment;
        switch (dateReference.direction) {
            case -1 /* MovesKeys.MOVE_BACKWARD */:
                newEndMoment = handleBackwardMovement(dateMoment);
                break;
            case 1 /* MovesKeys.MOVE_FORWARD */:
                newEndMoment = handleForwardMovement(dateMoment);
                break;
            default:
                console.error("Invalid direction value:", dateReference.direction);
                return;
        }
        const newEndDate = newEndMoment.format(dateFormat_1.DateFormat.us);
        const newStartDate = newEndMoment
            .clone()
            .date(1)
            .subtract(SEEN_MONTHS_NUMBER - 1, "months")
            .format(dateFormat_1.DateFormat.us);
        const newStartDateMoment = (0, moment_1.default)(newStartDate, dateFormat_1.DateFormat.us).date(1);
        const minCalendarMoment = (0, moment_1.default)(minCalendarDate, dateFormat_1.DateFormat.default).date(1);
        if (newStartDateMoment.isBefore(minCalendarMoment)) {
            setIsLeftButtonDisabled(true);
            return;
        }
        setVisibleDateRange({
            startDate: newStartDate,
            endDate: newEndDate,
        });
        setDateReference((prevState) => (Object.assign(Object.assign({}, prevState), { currentStartDate: newStartDate, currentEndDate: newEndDate, firstVisibleDataPointDate: newStartDate, lastVisibleDataPointDate: newEndDate })));
    }, [dateReference.triggerDirectionUpdate]);
    return {
        threeMonthsData,
        dateReference,
        isLeftButtonDisabled,
        isRightButtonDisabled,
        handleLeftClick: () => updateMoveValue(-1 /* MovesKeys.MOVE_BACKWARD */),
        handleRightClick: () => updateMoveValue(1 /* MovesKeys.MOVE_FORWARD */),
    };
};
exports.default = useCalendarHook;
